import FirebaseAdapter from "@wc/adapters/firebase";
import { PermissionsEnum, userHasPermissionInOrg } from "@wc/domain/authz";

import { INVITE_DEFAULTS } from "../constants";

/**
 * @typedef {import('@wc/domain/users/models').AuthnUser} AuthnUser
 */

/**
 * Creates a team invite link
 * @param {object} params Invite parameters
 * @param {string} params.teamId The team to invite to
 * @param {string} params.organizationId The org the team belongs to
 * @param {number} [params.expirationDays] Days until expiration (default: 7)
 * @param {number|null} [params.maxUses] Maximum uses (null = unlimited)
 * @param {AuthnUser} user The creating user
 * @returns {Promise<string>} The invite token
 */
export async function createTeamInvite(
  { teamId, organizationId, expirationDays, maxUses },
  user,
) {
  const hasPermission = await userHasPermissionInOrg(
    user,
    organizationId,
    PermissionsEnum.INVITE_ORGANIZATION_MEMBER,
  );

  if (!hasPermission) {
    throw new Error("You do not have permission to create invites");
  }

  const team = await FirebaseAdapter().teams.getTeam(teamId);
  if (!team || team.organizationId !== organizationId) {
    throw new Error("Team not found or does not belong to this organization");
  }

  const org =
    await FirebaseAdapter().organizations.getOrganization(organizationId);
  if (!org) {
    throw new Error("Organization not found");
  }

  const token = crypto.randomUUID();
  const days = expirationDays ?? INVITE_DEFAULTS.EXPIRATION_DAYS;
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + days);

  await FirebaseAdapter().invites.createInvite({
    id: token,
    teamId,
    organizationId,
    teamName: team.name,
    orgName: org.displayName || org.name,
    createdBy: user.id,
    expiresAt,
    useCount: 0,
    maxUses: maxUses ?? INVITE_DEFAULTS.MAX_USES,
    isActive: true,
  });

  return token;
}
